import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";

const SPEED = 0.6;
const DRAG_THRESHOLD = 10;

function TagPill({ tag, blockClick }) {
  return (
    <Link
      href={`/${tag.slug}`}
      draggable={false}
      onClick={(e) => {
        if (blockClick.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      className="inline-flex h-[60px] shrink-0 select-none items-center justify-center rounded-full px-[15px] bg-[#F5F7F9] text-[#274DE0] text-[18px] font-[500] hover:opacity-70 transition-opacity duration-300 whitespace-nowrap"
    >
      {tag.label}
    </Link>
  );
}

function TagsRow({ tags, keyPrefix, blockClick, hidden }) {
  return (
    <div
      className="flex shrink-0 items-center gap-[12px] md:gap-[16px] pr-[12px] md:pr-[16px]"
      aria-hidden={hidden || undefined}
    >
      {tags.map((tag, index) => (
        <TagPill key={`${keyPrefix}-${tag.slug}-${index}`} tag={tag} blockClick={blockClick} />
      ))}
    </div>
  );
}

export default function SeoTagsSlider({ tags = [] }) {
  const loopTags = useMemo(() => {
    if (!tags.length) return [];
    const repeats = Math.max(2, Math.ceil(12 / tags.length));
    return Array.from({ length: repeats }, () => tags).flat();
  }, [tags]);

  const trackRef = useRef(null);
  const offset = useRef(0);
  const loopWidth = useRef(0);
  const paused = useRef(false);
  const dragging = useRef(false);
  const pointerId = useRef(null);
  const dragStartX = useRef(0);
  const dragStartOffset = useRef(0);
  const wasDragged = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const measure = () => {
      loopWidth.current = track.scrollWidth / 2;
    };

    measure();
    window.addEventListener("resize", measure);

    let raf = 0;
    const animate = () => {
      if (!paused.current && !dragging.current && loopWidth.current) {
        offset.current -= SPEED;
        const width = loopWidth.current;
        while (offset.current <= -width) offset.current += width;
        track.style.transform = `translate3d(${offset.current}px, 0, 0)`;
      }
      raf = requestAnimationFrame(animate);
    };

    raf = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
    };
  }, [loopTags]);

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    dragging.current = true;
    pointerId.current = e.pointerId;
    dragStartX.current = e.clientX;
    dragStartOffset.current = offset.current;
    wasDragged.current = false;
    setIsDragging(true);
  };

  const onPointerMove = (e) => {
    if (!dragging.current || pointerId.current !== e.pointerId) return;
    const delta = e.clientX - dragStartX.current;
    if (Math.abs(delta) > DRAG_THRESHOLD) wasDragged.current = true;
    const width = loopWidth.current;
    let next = dragStartOffset.current + delta;
    while (next <= -width) next += width;
    while (next > 0) next -= width;
    offset.current = next;
    if (trackRef.current) trackRef.current.style.transform = `translate3d(${next}px, 0, 0)`;
  };

  const onPointerUp = (e) => {
    if (!dragging.current || pointerId.current !== e.pointerId) return;
    dragging.current = false;
    pointerId.current = null;
    setIsDragging(false);
    setTimeout(() => { wasDragged.current = false; }, 0);
  };

  if (!tags.length) return null;

  return (
    <section className="bg-white py-[20px] md:py-[40px]">
      <div
        className={`seo-tags-slider overflow-hidden ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
        style={{ touchAction: "pan-y" }}
        onMouseEnter={() => { paused.current = true; }}
        onMouseLeave={() => { if (!dragging.current) paused.current = false; }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div ref={trackRef} className="seo-tags-track flex w-max will-change-transform">
          <TagsRow tags={loopTags} keyPrefix="a" blockClick={wasDragged} />
          <TagsRow tags={loopTags} keyPrefix="b" blockClick={wasDragged} hidden />
        </div>
      </div>
    </section>
  );
}
